"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import { Autoplay, EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow, RightArrow, StartIcon, StopSign, transitionTime, useIsDesktop } from "ui"
import {
  extractProperty,
  getLinkHrefFromMultiUrlPickerItem,
  getUmbracoMediaUrl,
  replaceRichTextLinks,
} from "utils"
import { useAnchorScroll } from "../useAnchorScroll"
import { useTypingAnimation } from "../hooks/useTypingAnimation"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Styled Components (mobile-first, breakpoints.up only) ---

const Section = styled.section`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  height: 700px;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 900px;
  }
`

const BackgroundSlider = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;

  @keyframes hero5ZoomOut {
    from {
      transform: scale(1.15);
    }
    to {
      transform: scale(1);
    }
  }

  .swiper-slide-active .hero5-zoom-image {
    animation: hero5ZoomOut 5300ms ease-out forwards;
  }

  .swiper-slide:not(.swiper-slide-active) .hero5-zoom-image {
    transform: scale(1.15);
  }

  .swiper {
    width: 100%;
    height: 100%;
    padding-block: 0;
  }

  .swiper-slide {
    position: relative;
    width: 100%;
    height: 100%;
    opacity: 0 !important;
  }

  .swiper-slide-active {
    opacity: 1 !important;
  }
`

const StaticBgContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;

  @keyframes hero5ZoomOut {
    from {
      transform: scale(1.15);
    }
    to {
      transform: scale(1);
    }
  }

  .hero5-zoom-image {
    animation: hero5ZoomOut 5300ms ease-out forwards;
  }
`

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5) 30%, rgba(102, 102, 102, 0) 99%);
  pointer-events: none;
`

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex: 1;
  padding: 32px 16px 40px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 120px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 64px 240px;
  }
`

const MobileContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const DesktopRow1 = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    align-items: flex-end;
    gap: 40px;
  }
`

const DesktopRow2 = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
    gap: 40px;
  }
`

const DesktopCtaArea = styled.div`
  flex-shrink: 0;
`

const DesktopRightArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const LogosRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 8px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
    width: 425px;
    height: 128px;
  }
`

const Headline = styled.h1`
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.8px;
  color: white;
  text-shadow: 0 0 24px #07101d;
  margin: 0;
  width: 100%;
  position: relative;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 72px;
    letter-spacing: normal;
    text-align: start;
  }
`

const HeadlinePlaceholder = styled.span`
  display: block;
  visibility: hidden;
`

const HeadlineTyped = styled.span`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
`

const BodyText = styled.div`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: -0.36px;
  color: white;

  p {
    margin: 0;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
  }
`

const MobileCtaButton = styled(Button)`
  && {
    width: 100%;
    height: auto;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    text-transform: none;
    background-color: var(--inner-bg);
    color: var(--inner-text);

    &:hover {
      background-color: var(--inner-bg);
      opacity: 0.9;
    }
  }
`

const DesktopCtaButton = styled(Button)`
  && {
    width: 356px;
    height: 48px;
    border-radius: 8px;
    font-size: 20px;
    font-weight: 700;
    text-transform: none;
    background-color: var(--inner-bg);
    color: var(--inner-text);

    &:hover {
      background-color: var(--inner-bg);
      opacity: 0.9;
    }
  }
`

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  gap: 8px;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const SliderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }
`

const ArrowBtn = styled.button`
  background: white;
  border: 1px solid black;
  border-radius: 50px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  svg {
    color: black;
    path {
      fill: black;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

const PlayPauseBtn = styled.button`
  background: white;
  border: none;
  border-radius: 40px;
  padding: 10px;
  opacity: 0.4;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

const VideoPlayPauseBtn = styled.button`
  position: absolute;
  top: 20px;
  inset-inline-start: 20px;
  z-index: 3;
  background: rgba(255, 255, 255, 0.4);
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

const TabBarsContainer = styled.div`
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 1024px;
    gap: 16px;
    height: 56px;
  }
`

const tabFillAnimation = `
  @keyframes tabFill {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }
`

const TabBar = styled.button<{
  active: boolean
  filling: boolean
  past: boolean
}>`
  ${tabFillAnimation}
  flex: 1;
  height: 4px;
  padding: 0;
  border: none;
  background: #aaa;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background: white;
    transform-origin: left;
    transform: ${({ past }) => (past ? "scaleX(1)" : "scaleX(0)")};
    animation: ${({ filling }) => (filling ? "tabFill 5300ms linear forwards" : "none")};
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

// --- Sub-components ---

function SingleMedia({ item }: { item: any }) {
  const url = item?.url || ""
  const isVideo = url.endsWith(".mp4") || url.endsWith(".webm")

  if (isVideo) {
    return (
      <video
        src={getUmbracoMediaUrl(url)}
        autoPlay
        muted
        loop
        playsInline
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    )
  }

  return (
    <Image
      src={getUmbracoMediaUrl(url)}
      fill
      alt={item?.altText || ""}
      style={{ objectFit: "cover" }}
      className="hero5-zoom-image"
    />
  )
}

// --- Main Component ---

function HeroOption5({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const logos = extractProperty(data, "heroLogos", true) as any[] | undefined
  const headline = extractProperty(data, "heroHeadline") as string | undefined
  const bodyText = extractProperty(data, "heroBodyText") as string | undefined
  const ctaButton = extractProperty(data, "heroCtaButton") as any[] | undefined
  const desktopMedia = extractProperty(data, "heroBackgroundMedia", true) as any[] | undefined
  const mobileMedia = extractProperty(data, "heroBackgroundMediaMobile", true) as any[] | undefined
  const isDesktop = useIsDesktop()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const mediaItems = mounted && !isDesktop && mobileMedia?.length ? mobileMedia : desktopMedia

  const isRtl = locale === "he" || !locale
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [fillKey, setFillKey] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [videoPaused, setVideoPaused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { ref: headlineRef, inView: headlineInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  })
  const { displayed: typedHeadline, full: fullHeadline } = useTypingAnimation(
    headline,
    headlineInView,
  )

  const hasMedia = Array.isArray(mediaItems) && mediaItems.length > 0
  const hasMultipleMedia = hasMedia && mediaItems!.length > 1
  const currentUrl = hasMedia ? mediaItems![hasMultipleMedia ? activeIndex : 0]?.url : ""
  const currentIsVideo = currentUrl?.endsWith(".mp4") || currentUrl?.endsWith(".webm")

  const toggleVideo = useCallback(() => {
    const video = containerRef.current?.querySelector("video")
    if (!video) return
    if (video.paused) video.play()
    else video.pause()
    setVideoPaused(video.paused)
  }, [])
  const showArrows = hasMultipleMedia
  const showTabs = hasMultipleMedia
  const showPlayPause = hasMultipleMedia

  const togglePlayPause = useCallback(() => {
    if (swiperRef) {
      if (isPlaying) {
        swiperRef.autoplay.stop()
      } else {
        swiperRef.autoplay.start()
      }
    }
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.hero)
    setIsPlaying(!isPlaying)
  }, [swiperRef, isPlaying, analytics])

  useEffect(() => {
    if (!containerRef.current || !hasMultipleMedia) return
    const slides = containerRef.current.querySelectorAll(".swiper-slide")
    slides.forEach((slide, i) => {
      if (i === activeIndex) {
        slide.removeAttribute("inert")
        slide.removeAttribute("aria-hidden")
      } else {
        slide.setAttribute("inert", "")
        slide.setAttribute("aria-hidden", "true")
      }
    })
  }, [activeIndex, hasMultipleMedia])

  const ctaLink = ctaButton?.[0]
  const ctaHref = ctaLink ? getLinkHrefFromMultiUrlPickerItem(ctaLink as any) : undefined
  const handleAnchorClick = useAnchorScroll()
  const logoItems = Array.isArray(logos) ? logos : []
  const mobileLogoSize = { width: 134, height: 83 }
  const desktopLogoSize = { width: 157, height: 97 }

  return (
    <Section ref={containerRef}>
      {/* Background: full-screen image(s) */}
      {hasMedia && (
        <>
          {mediaItems!.length === 1 ? (
            <StaticBgContainer>
              <SingleMedia item={mediaItems![0]} />
            </StaticBgContainer>
          ) : (
            <BackgroundSlider dir="ltr">
              <Swiper
                loop
                onSwiper={setSwiperRef}
                onSlideChange={(swiper) => {
                  setActiveIndex(swiper.realIndex)
                  setFillKey((k) => k + 1)
                  setVideoPaused(false)
                  const activeSlide = swiper.slides[swiper.activeIndex]
                  const video = activeSlide?.querySelector("video")
                  if (video) {
                    video.currentTime = 0
                    video.play()
                  }
                }}
                modules={[EffectFade, Autoplay]}
                effect="fade"
                speed={transitionTime * 1000}
                style={{ width: "100%", height: "100%" }}
                autoplay={
                  isPlaying
                    ? {
                        delay: 4500,
                        disableOnInteraction: false,
                      }
                    : false
                }
              >
                {mediaItems!.map((item: any, i: number) => (
                  <SwiperSlide key={i}>
                    <SingleMedia item={item} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </BackgroundSlider>
          )}
        </>
      )}

      {/* Gradient overlay */}
      <GradientOverlay />

      {/* Content overlaid on the background */}
      <ContentWrapper>
        {/* Mobile layout */}
        <MobileContent>
          {logoItems.length > 0 && (
            <LogosRow>
              {logoItems.map((logo: any, i: number) => (
                <Image
                  key={i}
                  src={getUmbracoMediaUrl(logo?.url || "/")}
                  width={mobileLogoSize.width}
                  height={mobileLogoSize.height}
                  alt={logo?.altText || ""}
                  style={{ objectFit: "contain" }}
                />
              ))}
            </LogosRow>
          )}

          {headline && (
            <Headline ref={isDesktop ? undefined : headlineRef}>
              <HeadlinePlaceholder>{fullHeadline}</HeadlinePlaceholder>
              <HeadlineTyped>{typedHeadline}</HeadlineTyped>
            </Headline>
          )}

          {bodyText && (
            <BodyText
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(bodyText, true),
              }}
            />
          )}

          {ctaLink && (
            <MobileCtaButton
              variant="contained"
              href={ctaHref}
              onClick={(e) => {
                analytics.ctaClick(ctaLink.name, LANDING_ELEMENTS.hero)
                handleAnchorClick(e, ctaHref)
              }}
              disableElevation
            >
              {ctaLink.name}
            </MobileCtaButton>
          )}
        </MobileContent>

        {/* Desktop layout - Row 1: Logos & Headline (first=right in RTL) + CTA (second=left in RTL) */}
        <DesktopRow1>
          <DesktopRightArea>
            {logoItems.length > 0 && (
              <LogosRow>
                {logoItems.map((logo: any, i: number) => (
                  <Image
                    key={i}
                    src={getUmbracoMediaUrl(logo?.url || "/")}
                    width={desktopLogoSize.width}
                    height={desktopLogoSize.height}
                    alt={logo?.altText || ""}
                    style={{ objectFit: "contain" }}
                  />
                ))}
              </LogosRow>
            )}
            {headline && (
              <Headline ref={isDesktop ? headlineRef : undefined}>
                <HeadlinePlaceholder>{fullHeadline}</HeadlinePlaceholder>
                <HeadlineTyped>{typedHeadline}</HeadlineTyped>
              </Headline>
            )}
            {bodyText && (
              <BodyText
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(bodyText, true),
                }}
              />
            )}
          </DesktopRightArea>
          <DesktopCtaArea>
            {ctaLink && (
              <DesktopCtaButton
                variant="contained"
                href={ctaHref}
                onClick={(e) => {
                  analytics.ctaClick(ctaLink.name, LANDING_ELEMENTS.hero)
                  handleAnchorClick(e, ctaHref)
                }}
                disableElevation
              >
                {ctaLink.name}
              </DesktopCtaButton>
            )}
          </DesktopCtaArea>
        </DesktopRow1>

        {/* Desktop layout - Row 2: Tabs (first=right in RTL) + Controls (second=left in RTL) */}
        {(showPlayPause || showTabs) && (
          <DesktopRow2>
            {showTabs && (
              <TabBarsContainer>
                {mediaItems!.map((_: any, i: number) => (
                  <TabBar
                    key={`${i}-${fillKey}`}
                    active={i === activeIndex}
                    filling={i === activeIndex && isPlaying}
                    past={i < activeIndex}
                    onClick={() => swiperRef?.slideTo(i)}
                    aria-label={isRtl ? `עבור לשקופית ${i + 1}` : `Go to slide ${i + 1}`}
                    aria-current={i === activeIndex ? "true" : undefined}
                  />
                ))}
              </TabBarsContainer>
            )}

            <SliderControls>
              {showArrows && (
                <ArrowBtn
                  onClick={() => swiperRef?.slidePrev()}
                  aria-label={isRtl ? "לתמונה הקודמת" : "Previous slide"}
                >
                  {isRtl ? <RightArrow fill="currentColor" /> : <LeftArrow fill="currentColor" />}
                </ArrowBtn>
              )}

              {showPlayPause && (
                <PlayPauseBtn
                  onClick={togglePlayPause}
                  aria-label={
                    isPlaying
                      ? isRtl
                        ? "עצור תנועת הרכיב"
                        : "Stop animation"
                      : isRtl
                      ? "הפעל תנועה רכיב"
                      : "Play animation"
                  }
                >
                  {isPlaying ? <StopSign /> : <StartIcon />}
                </PlayPauseBtn>
              )}

              {showArrows && (
                <ArrowBtn
                  onClick={() => swiperRef?.slideNext()}
                  aria-label={isRtl ? "לתמונה הבאה" : "Next slide"}
                >
                  {isRtl ? <LeftArrow fill="currentColor" /> : <RightArrow fill="currentColor" />}
                </ArrowBtn>
              )}
            </SliderControls>
          </DesktopRow2>
        )}

        {/* Mobile bottom row: controls + tab bars */}
        {(showPlayPause || showTabs) && (
          <BottomRow>
            {showTabs && (
              <TabBarsContainer>
                {mediaItems!.map((_: any, i: number) => (
                  <TabBar
                    key={`${i}-${fillKey}`}
                    active={i === activeIndex}
                    filling={i === activeIndex && isPlaying}
                    past={i < activeIndex}
                    onClick={() => swiperRef?.slideTo(i)}
                    aria-label={isRtl ? `עבור לשקופית ${i + 1}` : `Go to slide ${i + 1}`}
                    aria-current={i === activeIndex ? "true" : undefined}
                  />
                ))}
              </TabBarsContainer>
            )}
            <SliderControls>
              {showArrows && (
                <ArrowBtn
                  onClick={() => swiperRef?.slidePrev()}
                  aria-label={isRtl ? "לתמונה הקודמת" : "Previous slide"}
                >
                  {isRtl ? <RightArrow fill="currentColor" /> : <LeftArrow fill="currentColor" />}
                </ArrowBtn>
              )}

              {showPlayPause && (
                <PlayPauseBtn
                  onClick={togglePlayPause}
                  aria-label={
                    isPlaying
                      ? isRtl
                        ? "עצור תנועת הרכיב"
                        : "Stop animation"
                      : isRtl
                      ? "הפעל תנועה רכיב"
                      : "Play animation"
                  }
                >
                  {isPlaying ? <StopSign /> : <StartIcon />}
                </PlayPauseBtn>
              )}

              {showArrows && (
                <ArrowBtn
                  onClick={() => swiperRef?.slideNext()}
                  aria-label={isRtl ? "לתמונה הבאה" : "Next slide"}
                >
                  {isRtl ? <LeftArrow fill="currentColor" /> : <RightArrow fill="currentColor" />}
                </ArrowBtn>
              )}
            </SliderControls>
          </BottomRow>
        )}
      </ContentWrapper>

      {currentIsVideo && (
        <VideoPlayPauseBtn
          onClick={toggleVideo}
          aria-label={
            videoPaused
              ? isRtl
                ? "הפעל וידאו"
                : "Play video"
              : isRtl
              ? "עצור וידאו"
              : "Pause video"
          }
        >
          {videoPaused ? <StartIcon /> : <StopSign />}
        </VideoPlayPauseBtn>
      )}
    </Section>
  )
}

export default HeroOption5
